import React, { useState, useEffect } from 'react';
import { AWS_CONFIG } from '../config/aws';

export default function FAQPage() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  // FAQ 데이터 fetch
  useEffect(() => {
    let cancelled = false;
    async function loadFaqs() {
      try {
        const res = await fetch(`${AWS_CONFIG.apiUrl}/faqs`);
        const data = await res.json();
        if (!cancelled) setFaqs(data.faqs || []);
      } catch (err) {
        console.error('[FAQPage:loadFaqs] error', err);
        if (!cancelled) setFaqs([]);
      }
      if (!cancelled) setLoading(false);
    }
    loadFaqs();
    return () => { cancelled = true; };
  }, []);

  // 0건일 때 noindex 메타 삽입
  useEffect(() => {
    if (loading) return;
    if (faqs.length > 0) return;
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    meta.setAttribute('data-faq-noindex', 'true');
    document.head.appendChild(meta);
    return () => {
      const existing = document.head.querySelector('meta[data-faq-noindex]');
      if (existing) document.head.removeChild(existing);
    };
  }, [faqs, loading]);

  // Schema.org FAQPage JSON-LD (1건 이상일 때만)
  useEffect(() => {
    if (loading || faqs.length === 0) return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-faq-jsonld', 'true');
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map(f => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    });
    document.head.appendChild(script);
    return () => {
      const existing = document.head.querySelector('script[data-faq-jsonld]');
      if (existing) document.head.removeChild(existing);
    };
  }, [faqs, loading]);

  if (loading) {
    return (
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '80px 20px 40px' }}>
        <p style={{ color: 'var(--text-muted)' }}>로딩 중...</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '80px 20px 60px' }}>
      <h1 style={{
        fontSize: '2rem', fontWeight: 700,
        color: 'var(--text)', marginBottom: 12,
      }}>
        자주 묻는 질문
      </h1>
      <p style={{
        fontSize: '1rem', color: 'var(--text-muted)',
        marginBottom: 40, lineHeight: 1.6,
      }}>
        KOA DETAILING 이용 전 궁금한 점을 모았습니다. 더 궁금한 사항은 카카오톡으로 문의해 주세요.
      </p>

      {faqs.length === 0 ? (
        <div style={{
          padding: '40px 24px', textAlign: 'center',
          background: 'var(--surface)', borderRadius: 'var(--radius)',
          color: 'var(--text-muted)',
        }}>
          <p style={{ fontSize: '1.1rem', margin: 0 }}>준비 중입니다.</p>
          <p style={{ fontSize: '0.9rem', marginTop: 8 }}>
            곧 자주 묻는 질문을 안내해 드리겠습니다.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {faqs.map((faq, idx) => (
            <details
              key={faq.faqId}
              style={{
                borderTop: idx === 0 ? '1px solid var(--border)' : 'none',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <summary style={{
                padding: '18px 4px', cursor: 'pointer',
                fontWeight: 600, fontSize: '1rem',
                color: 'var(--text)', listStyle: 'none',
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: 12,
                userSelect: 'none',
              }}>
                <span>
                  <span style={{ color: 'var(--primary)', marginRight: 10 }}>Q.</span>
                  {faq.question}
                </span>
                <span style={{
                  fontSize: '1.2rem', color: 'var(--text-muted)',
                  flexShrink: 0,
                }}>
                  ＋
                </span>
              </summary>
              <div style={{
                padding: '0 4px 20px 28px',
                color: 'var(--text-muted)', fontSize: '0.95rem',
                lineHeight: 1.7, whiteSpace: 'pre-wrap',
              }}>
                {faq.answer}
              </div>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}
